import React, { useState, useEffect } from 'react';

/**
 * SavingsOpportunities
 *
 * Coordinator view of the best opportunities to increase Kin quantities
 * to unlock volume pricing tiers.
 *
 * Shows the top opportunities with the members ordering each product,
 * the current price and quantity, what's needed to hit the next tier,
 * and a button to notify those members.
 *
 * Moved out of KinDashboard.js so it can be used on the Kin Admin page.
 */
export default function SavingsOpportunities({ kinId }) {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalSavings, setTotalSavings] = useState(0);
  const [sending, setSending] = useState(null);
  const [sentStatus, setSentStatus] = useState({});

  useEffect(() => {
    if (kinId) {
      fetchOpportunities();
    }
  }, [kinId]);

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/kin-savings/kin/${kinId}/opportunities`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setOpportunities(data.opportunities || []);
        setTotalSavings(data.totalSavingsPotential || 0);
      } else {
        const err = await res.json();
        setError(err.error || 'Failed to load opportunities');
      }
    } catch (err) {
      console.error('Error fetching opportunities:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendSuggestion = async (opportunity) => {
    if (!window.confirm(`Send suggestion to members about ${opportunity.productName}?`)) return;

    setSending(opportunity.productId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/kin-savings/kin/${kinId}/suggest`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: opportunity.productId,
          productName: opportunity.productName,
          additionalNeeded: opportunity.opportunity.additionalNeeded,
          savingsPerUnit: opportunity.opportunity.savingsPerUnit,
          totalSavings: opportunity.potentialTotalSavings,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setSentStatus((prev) => ({ ...prev, [opportunity.productId]: true }));
        alert(`✅ ${data.message}`);
      } else {
        const err = await res.json();
        alert(`❌ ${err.error || 'Failed to send suggestions'}`);
      }
    } catch (err) {
      console.error('Error sending suggestions:', err);
      alert('❌ Failed to send suggestions');
    } finally {
      setSending(null);
    }
  };

  if (loading) {
    return <div className="text-gray-500">Analyzing savings opportunities...</div>;
  }

  if (error) {
    return <div className="text-red-600">Error: {error}</div>;
  }

  if (opportunities.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 text-center">
        <p className="text-gray-500">No volume pricing opportunities available.</p>
        <p className="text-sm text-gray-400 mt-1">Members may already be at the best price tier.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold">💎 Savings Opportunities</h3>
          <p className="text-sm text-gray-500">
            Add a few more items to unlock volume discounts for the whole Kin
          </p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-center">
          <p className="text-xs text-green-600">Potential Savings</p>
          <p className="text-lg font-bold text-green-700">${totalSavings.toFixed(2)}</p>
        </div>
      </div>

      <div className="space-y-3">
        {opportunities.map((opp, idx) => {
          const savingsPerItem = opp.opportunity.savingsPerUnit || 0;
          const totalSavings = opp.opportunity.totalSavings || 0;
          const additionalNeeded = opp.opportunity.additionalNeeded || 0;
          const newPrice = opp.opportunity.tier?.price || 0;
          const currentPrice = opp.currentUnitPrice || 0;
          const isSent = sentStatus[opp.productId];
          const isSending = sending === opp.productId;

          return (
            <div key={idx} className="border border-blue-200 bg-blue-50 rounded-lg p-4 hover:shadow-md transition">
              <div className="flex flex-wrap justify-between items-start gap-2">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-lg font-semibold text-blue-800">#{idx + 1}</span>
                    <h4 className="font-medium text-gray-800">{opp.productName}</h4>
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                      {opp.members.length} members ordering
                    </span>
                    {isSent && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                        ✅ Suggestions Sent
                      </span>
                    )}
                  </div>

                  <div className="mt-1 flex flex-wrap gap-3 text-sm">
                    <span className="text-gray-600">
                      Current: <span className="font-medium">{opp.currentQuantity} units</span>
                    </span>
                    <span className="text-gray-600">
                      Current Price: <span className="font-medium">${currentPrice.toFixed(2)}</span>
                    </span>
                    <span className="text-gray-600">
                      New Price: <span className="font-medium text-green-600">${newPrice.toFixed(2)}</span>
                    </span>
                    <span className="text-gray-600">
                      Need: <span className="font-medium text-blue-600">+{additionalNeeded} more</span>
                    </span>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-1">
                    {opp.members.slice(0, 5).map((member, i) => (
                      <span key={i} className="text-xs bg-white px-2 py-0.5 rounded border border-gray-200">
                        {member.memberName}: {member.quantity}
                      </span>
                    ))}
                    {opp.members.length > 5 && (
                      <span className="text-xs text-gray-400">+{opp.members.length - 5} more</span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 min-w-[120px]">
                  <div className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm font-medium text-center w-full">
                    Save ${totalSavings.toFixed(2)}
                  </div>
                  {savingsPerItem > 0 && (
                    <div className="text-xs text-gray-500 text-center w-full">
                      ${savingsPerItem.toFixed(2)} saved per unit
                    </div>
                  )}
                  <button
                    onClick={() => sendSuggestion(opp)}
                    disabled={isSending || isSent}
                    className={`w-full text-sm px-3 py-1.5 rounded transition-colors ${
                      isSent
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : isSending
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isSending ? 'Sending...' : isSent ? '✅ Sent' : '📧 Suggest to Members'}
                  </button>
                  {isSent && (
                    <div className="text-xs text-gray-400 text-center">
                      Suggestions sent to {opp.members.length} members
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 bg-gray-50 rounded-lg p-3 text-sm text-gray-500">
        <p>💡 <strong>Tip:</strong> Suggestions are sent only to members who ordered the product.</p>
        <p className="mt-1">Members can adjust their orders until you finalize the consolidation.</p>
      </div>
    </div>
  );
}
